import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useEditor, DEFAULT_TEXT_PROPS } from '../context/EditorContext';
import { isTextType, commonProp, toggleToken, isShapeWithText } from '../lib/selection';
import { SHAPE_TEXT_DEFAULTS, SHAPE_TEXT_KEYS } from '../lib/shapeText';
import { isEditingText, execEditCommand, applyInlineStyle, applyInlineScale, applyTypingStyle, hasTextSelection, queryState, insertTextAtCaret, copySelectedText, cutSelectedText, pasteTextAtCaret, hasTextClip, selectionFontSize, selectionFontFamily, selectionColor, selectionCharacterStyle, selectWordAtCaret, restoreCaretInWord, editedBoxHasText, applyParagraphStyle, selectionParagraphStyle, paragraphPropsToStyle, clearEditedParagraphStyles, setBlankLineFontSize } from '../lib/editingStore';
import { changeCase, trackingToLetterSpacing, clearParagraphStyles } from '../lib/textUtils';
import { pushRecentColor } from '../lib/colors';
import { previewLineSpacing as previewLineSpacingDom, endLineSpacingPreview } from '../lib/spacingPreview';
import { ensureFont } from '../lib/fontLoader';

import { FONT_SIZES_PT, pxToPt, ptToPx } from '../lib/units';

/** Font sizes are stored in page px; the UI talks points (Publisher). The size list and Grow / Shrink step in points. */
const FONT_SIZES = FONT_SIZES_PT;
const ALIGN_CMD = { left: 'justifyLeft', center: 'justifyCenter', right: 'justifyRight', justify: 'justifyFull' };
/** Paragraph properties (Publisher): set on the highlighted paragraphs while editing, on the box otherwise. */
const PARA_KEYS = ['lineHeight', 'spaceBefore', 'spaceAfter', 'indentLeft', 'firstLineIndent', 'textAlign'];
export const FONT_SIZE_LIST = FONT_SIZES;

/**
 * Text formatting commands, Publisher-style:
 *  - text highlighted in the box being edited → that text only (inline spans; a box can mix fonts, sizes, colours…);
 *  - just a caret inside a word → that word;
 *  - just a caret on a blank line → the font *size* resizes that line (the paragraph mark's size, as in Publisher / Word);
 *  - just a caret elsewhere in a box with text → the "typing" style for what is typed next (font / size / bold / colour…);
 *  - otherwise (nothing being edited, or an empty box) → the whole selected text / table object(s).
 * The same hook drives the ribbon tabs, the Properties pane, the Font dialog and the context menu so they all behave alike.
 */
export const useTextCommands = () => {
  const { state, dispatch, rawDispatch, getSelectedObjects, getCurrentPage } = useEditor();
  const selected = getSelectedObjects();
  const textObjs = useMemo(() => selected.filter((o) => isTextType(o) || o.type === 'table' || isShapeWithText(o, state.editingObjectId)), [selected, state.editingObjectId]);
  const ids = useMemo(() => textObjs.map((o) => o.id), [textObjs]);
  const editing = isEditingText() && state.editingObjectId;
  const primary = textObjs[0];
  // Shapes that hold text start from the shape-text defaults (centred, middle) rather than the text-box defaults.
  const p = useMemo(() => ({ ...DEFAULT_TEXT_PROPS, ...(primary?.type === 'shape' ? SHAPE_TEXT_DEFAULTS : null), ...(primary?.properties || {}) }), [primary]);

  // While editing, re-render when the caret / selection moves so the ribbon shows the font, size and bold / italic state
  // of the text under the caret (a box may mix several) rather than the box defaults.
  const [, bump] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    if (!editing) return undefined;
    let raf = 0;
    const onSel = () => { if (!raf) raf = window.requestAnimationFrame(() => { raf = 0; bump(); }); };
    document.addEventListener('selectionchange', onSel);
    return () => { document.removeEventListener('selectionchange', onSel); if (raf) window.cancelAnimationFrame(raf); };
  }, [editing]);

  /** Font family / size shown in the pickers: the text under the caret while editing, else the box's. */
  const currentFontFamily = editing ? selectionFontFamily(p.fontFamily) : p.fontFamily;
  const currentFontSize = editing ? selectionFontSize(p.fontSize || 16) : p.fontSize;
  /** Text colour shown by the ribbon swatch / properties pane: the text under the caret while editing, else the box's. */
  const currentColor = editing ? selectionColor(p.color || '#000000') : (p.color || '#000000');
  /** Character formatting at the caret / selection start while editing (dialogs start from it), else {}. */
  const characterStyle = useCallback(() => (editing ? selectionCharacterStyle(p) : {}), [editing, p]);
  /** Paragraph formatting the caret's paragraph sets itself while editing (line spacing, space before / after, indents, alignment), else {}. */
  const paragraphStyle = useCallback(() => (editing ? selectionParagraphStyle() : {}), [editing]);
  /** Line spacing shown by the ribbon: the caret's paragraph while editing (its own value, else the box's), else the box's. */
  const currentLineHeight = editing ? (selectionParagraphStyle().lineHeight ?? p.lineHeight ?? 1.2) : (p.lineHeight ?? 1.2);

  const setProps = useCallback((props, targetIds) => {
    const t = targetIds || ids;
    if (!t.length) return false;
    dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: t, props } });
    return true;
  }, [dispatch, ids]);

  /**
   * Where an inline command goes: 'selection' (highlighted text), 'word' (the caret's word — now selected), 'caret'
   * (typing style at a bare caret in a box that has text) or 'box' (whole object(s)).
   */
  const inlineScope = useCallback(() => {
    if (!editing) return 'box';
    if (hasTextSelection()) return 'selection';
    if (selectWordAtCaret()) return 'word';
    return editedBoxHasText() ? 'caret' : 'box';
  }, [editing]);

  /** Run an inline command for the given scope; the word scope puts the caret back inside the word afterwards. */
  const runInline = useCallback((scope, fn) => {
    const ok = fn();
    if (scope === 'word') restoreCaretInWord();
    return ok;
  }, []);

  const ensureTextTarget = useCallback(() => {
    if (ids.length) return ids;
    // no text selected: create a text box in the middle of the page
    const page = getCurrentPage();
    dispatch({ type: 'ADD_OBJECT', payload: { type: 'textbox', x: page.width / 2 - 150, y: page.height / 2 - 40, width: 300, height: 80, rotation: 0, properties: { ...DEFAULT_TEXT_PROPS, text: '', fontFamily: state.textSettings?.fontFamily || 'Inter', fontSize: state.textSettings?.fontSize || 16 } } });
    return null;
  }, [ids, getCurrentPage, dispatch, state.textSettings]);

  const toggle = useCallback((cmd) => {
    const scope = inlineScope();
    // bare caret: bold / italic become the typing style (survives a trip through a ribbon input); underline, strikethrough
    // and sub / superscript stay browser typing styles (turning them off inside decorated text needs the browser's splitting)
    if (scope === 'caret' && (cmd === 'bold' || cmd === 'italic')) {
      const on = queryState(cmd);
      return applyTypingStyle(cmd === 'bold' ? { fontWeight: on ? 'normal' : 'bold' } : { fontStyle: on ? 'normal' : 'italic' });
    }
    if (scope !== 'box') return runInline(scope, () => execEditCommand(cmd));
    if (!ids.length) return false;
    switch (cmd) {
      case 'bold': return setProps({ fontWeight: commonProp(textObjs, 'fontWeight', 'normal') === 'bold' ? 'normal' : 'bold' });
      case 'italic': return setProps({ fontStyle: commonProp(textObjs, 'fontStyle', 'normal') === 'italic' ? 'normal' : 'italic' });
      case 'underline': return setProps({ textDecoration: toggleToken(commonProp(textObjs, 'textDecoration', 'none'), 'underline') });
      case 'strikeThrough': return setProps({ textDecoration: toggleToken(commonProp(textObjs, 'textDecoration', 'none'), 'line-through') });
      case 'subscript': return setProps({ fontVariantPosition: p.fontVariantPosition === 'sub' ? 'normal' : 'sub' });
      case 'superscript': return setProps({ fontVariantPosition: p.fontVariantPosition === 'super' ? 'normal' : 'super' });
      default: return false;
    }
  }, [inlineScope, runInline, ids, setProps, textObjs, p]);

  const isActive = useCallback((cmd) => {
    if (editing) return queryState(cmd);
    if (!primary) return false;
    switch (cmd) {
      case 'bold': return p.fontWeight === 'bold' || p.fontWeight === 700 || p.fontWeight === '700';
      case 'italic': return p.fontStyle === 'italic';
      case 'underline': return String(p.textDecoration || '').includes('underline');
      case 'strikeThrough': return String(p.textDecoration || '').includes('line-through');
      case 'subscript': return p.fontVariantPosition === 'sub';
      case 'superscript': return p.fontVariantPosition === 'super';
      default: return false;
    }
  }, [editing, primary, p]);

  const setFontFamily = useCallback((family) => {
    dispatch({ type: 'SET_TEXT_SETTINGS', payload: { fontFamily: family } });
    ensureFont(family); // a family picked for a few words must be loaded like one picked for a whole box
    const scope = inlineScope();
    if (scope === 'caret') return applyTypingStyle({ fontFamily: /\s/.test(family) ? `"${family}"` : family });
    if (scope !== 'box') return runInline(scope, () => execEditCommand('fontName', family));
    return setProps({ fontFamily: family });
  }, [inlineScope, runInline, setProps, dispatch]);

  /**
   * Font picker hover preview: paints `family` on the selected text objects without touching the document (null clears).
   * Skipped while a box with text is being edited — the pick will only change the highlighted text / caret word / what is
   * typed next, so previewing the whole box would be misleading (the picker shows each family in its own face anyway).
   */
  const previewFontFamily = useCallback((family) => {
    if (family && editing && (hasTextSelection() || editedBoxHasText())) { rawDispatch({ type: 'SET_FONT_PREVIEW', payload: null }); return; }
    rawDispatch({ type: 'SET_FONT_PREVIEW', payload: family && ids.length ? { ids, family } : null });
  }, [rawDispatch, ids, editing]);

  const setFontSize = useCallback((size) => {
    // page px (the UI converts points at the edges, lib/units); 3 decimals so 11 pt (14.667 px) reads back as 11 pt
    const s = Math.round(Math.max(1, Math.min(400, parseFloat(size) || 12)) * 1000) / 1000;
    dispatch({ type: 'SET_TEXT_SETTINGS', payload: { fontSize: s } });
    // caret on a blank line: the line itself gets taller / shorter (presets, a typed size, Grow / Shrink alike)
    if (editing && !hasTextSelection() && setBlankLineFontSize(s)) return true;
    const scope = inlineScope();
    if (scope === 'selection' || scope === 'word') return runInline(scope, () => applyInlineStyle({ fontSize: `${s}px` }));
    // bare caret between words: the size of what is typed next (the browser has no typing style for a px size)
    if (scope === 'caret') return applyTypingStyle({ fontSize: `${s}px` });
    return setProps({ fontSize: s });
  }, [editing, inlineScope, runInline, setProps, dispatch]);

  const stepFontSize = useCallback((dir) => {
    const cur = pxToPt(currentFontSize || 16); // step through the point list, like the size box
    const idx = FONT_SIZES.findIndex((v) => v >= cur - 0.001);
    let next;
    if (dir > 0) next = FONT_SIZES[Math.min(FONT_SIZES.length - 1, (idx === -1 ? FONT_SIZES.length - 1 : Math.abs(FONT_SIZES[idx] - cur) < 0.001 ? idx + 1 : idx))] || cur + 2;
    else next = FONT_SIZES[Math.max(0, (idx === -1 ? FONT_SIZES.length : idx) - 1)] || Math.max(1, cur - 2);
    return setFontSize(ptToPx(next));
  }, [currentFontSize, setFontSize]);

  const setColor = useCallback((hex) => {
    if (hex) pushRecentColor(hex);
    const scope = inlineScope();
    if (scope === 'caret') return applyTypingStyle({ color: hex || '#000000' });
    if (scope !== 'box') return runInline(scope, () => execEditCommand('foreColor', hex || '#000000'));
    return setProps({ color: hex || '#000000', textFill: null });
  }, [inlineScope, runInline, setProps]);

  const setHighlight = useCallback((hex) => {
    const scope = inlineScope();
    if (scope === 'caret') return applyTypingStyle({ backgroundColor: hex || 'transparent' });
    if (scope !== 'box') return runInline(scope, () => execEditCommand('hiliteColor', hex || 'transparent'));
    return setProps({ background: hex ? { type: 'solid', color: hex } : { type: 'none' } });
  }, [inlineScope, runInline, setProps]);

  // Alignment is a paragraph property (Publisher): while editing it applies to the paragraph(s) at the caret / selection
  // only — the box's own alignment (used by paragraphs without one of their own) changes only when nothing is being edited.
  const setAlign = useCallback((align) => {
    if (editing) return execEditCommand(ALIGN_CMD[align]);
    return setProps({ textAlign: align });
  }, [editing, setProps]);

  /** Ribbon toggle state for an alignment: the caret paragraph while editing, else the box. */
  const alignActive = useCallback((align) => {
    if (editing) return queryState(ALIGN_CMD[align]);
    return (p.textAlign || 'left') === align;
  }, [editing, p.textAlign]);

  // Clipboard: highlighted text while editing, whole objects otherwise (the ribbon buttons and context menu use these)
  const copy = useCallback(() => { if (copySelectedText()) return true; if (!selected.length) return false; dispatch({ type: 'COPY_OBJECTS' }); return true; }, [selected.length, dispatch]);
  const cut = useCallback(() => { if (cutSelectedText()) return true; if (!selected.length) return false; dispatch({ type: 'CUT_OBJECTS' }); return true; }, [selected.length, dispatch]);
  const paste = useCallback(async () => { if (isEditingText()) return pasteTextAtCaret(); if (!state.clipboard.length) return false; dispatch({ type: 'PASTE_OBJECTS' }); return true; }, [state.clipboard.length, dispatch]);
  const canPaste = (isEditingText() && hasTextClip()) || state.clipboard.length > 0;

  const setList = useCallback((style) => {
    if (editing) return execEditCommand(style === 'bullet' ? 'insertUnorderedList' : 'insertOrderedList');
    return setProps({ listStyle: p.listStyle === style ? 'none' : style });
  }, [editing, setProps, p.listStyle]);

  const clearFormatting = useCallback(() => {
    const scope = inlineScope();
    if (scope === 'selection' || scope === 'word') return runInline(scope, () => execEditCommand('removeFormat'));
    // Text boxes reset to the full defaults; shapes only reset their text keys so fill / outline / effects stay intact.
    textObjs.forEach((o) => {
      if (o.type === 'shape') {
        const reset = { ...SHAPE_TEXT_DEFAULTS, html: undefined, text: o.properties?.text, fontFamily: p.fontFamily, fontSize: p.fontSize };
        for (const k of SHAPE_TEXT_KEYS) if (!(k in reset)) reset[k] = undefined;
        dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [o.id], props: reset } });
      } else dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [o.id], props: { ...DEFAULT_TEXT_PROPS, html: undefined, text: o.properties?.text, fontFamily: p.fontFamily, fontSize: p.fontSize } } });
    });
    return textObjs.length > 0;
  }, [inlineScope, runInline, textObjs, dispatch, p]);

  const applyCase = useCallback((mode) => {
    const scope = inlineScope();
    if (scope === 'selection' || scope === 'word') {
      const sel = window.getSelection().toString();
      return runInline(scope, () => insertTextAtCaret(changeCase(sel, mode)));
    }
    if (!ids.length) return false;
    textObjs.forEach((o) => dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [o.id], props: { text: changeCase(o.properties?.text || '', mode), html: undefined } } }));
    return true;
  }, [inlineScope, runInline, ids, textObjs, dispatch]);

  const setProp = useCallback((key, value) => setProps({ [key]: value }), [setProps]);

  // Paragraph formatting (Publisher): line spacing, space before / after and indents are paragraph properties.
  //  - text highlighted in the box being edited → the paragraphs the highlight touches (a partly highlighted paragraph
  //    changes as a whole) get inline paragraph styles — never the other paragraphs of the box, never other boxes;
  //  - nothing highlighted (a bare caret, or the box just selected) → the whole box: the box property is set and the
  //    paragraphs' own values of that property are cleared (in the live editable too), so every paragraph follows it.
  const applyParagraphProps = useCallback((patch) => {
    const keys = Object.keys(patch).filter((k) => PARA_KEYS.includes(k));
    if (!keys.length) return false;
    endLineSpacingPreview(); // a hovered preset is being picked: the preview goes before the real value is written
    if (editing && hasTextSelection()) return applyParagraphStyle(paragraphPropsToStyle(patch));
    if (editing) {
      clearEditedParagraphStyles(keys); // the stored html follows through the editable's sync
      const target = ids.length ? ids : [state.editingObjectId];
      dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: target, props: patch } });
      return true;
    }
    if (!ids.length) return false;
    textObjs.forEach((o) => {
      const html = o.properties?.html;
      const cleaned = html ? clearParagraphStyles(html, keys) : html;
      dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [o.id], props: { ...patch, ...(cleaned !== html ? { html: cleaned } : null) } } });
    });
    return true;
  }, [editing, ids, textObjs, dispatch, state.editingObjectId]);
  /** A multiple (1.5) or an exact height ("16px" — Exactly 12 pt, see lib/units). */
  const setLineSpacing = useCallback((value) => applyParagraphProps({ lineHeight: value }), [applyParagraphProps]);
  /** Home ▸ Line Spacing hover preview (lib/spacingPreview): `null` takes it back. Same scope as setLineSpacing; visual only. */
  const previewLineSpacing = useCallback((value) => (value ? previewLineSpacingDom(value, ids) : endLineSpacingPreview()), [ids]);

  /**
   * Dialogs (Font…): put a props patch on the highlighted text / the caret's word as inline styles, or make it the typing
   * style at a bare caret. Returns false when nothing inline is targeted (the caller then applies the patch to the whole
   * box with setProps).
   */
  const applyInlineProps = useCallback((patch) => {
    // Font dialog with the caret on a blank line: the size resizes the line; the other properties become the typing style
    const blankSized = !!patch.fontSize && editing && !hasTextSelection() && setBlankLineFontSize(Math.max(1, Math.min(400, parseFloat(patch.fontSize) || 12)));
    const scope = inlineScope();
    if (scope === 'box') return false; // (the whole-box patch the caller applies next covers the blank line's size as well)
    const css = {};
    if (patch.fontFamily) { css.fontFamily = `"${patch.fontFamily}"`; ensureFont(patch.fontFamily); }
    if (patch.fontSize && !blankSized) css.fontSize = `${patch.fontSize}px`;
    if (patch.fontWeight) css.fontWeight = String(patch.fontWeight);
    if (patch.fontStyle) css.fontStyle = patch.fontStyle;
    if (patch.textDecoration) css.textDecoration = patch.textDecoration;
    if (patch.color) css.color = patch.color;
    if (patch.textTransform) css.textTransform = patch.textTransform;
    if (patch.fontVariant) css.fontVariant = patch.fontVariant;
    const pos = patch.fontVariantPosition;
    if (!Object.keys(css).length && !pos) return blankSized;
    if (scope === 'caret') return Object.keys(css).length ? applyTypingStyle(css) || blankSized : blankSized;
    return runInline(scope, () => {
      const ok = Object.keys(css).length ? applyInlineStyle(css) : true;
      // super / subscript are execCommand toggles (they size + shift the text themselves); align them with the request
      if (pos === 'super' || pos === 'sub') { if (!queryState(pos === 'super' ? 'superscript' : 'subscript')) execEditCommand(pos === 'super' ? 'superscript' : 'subscript'); }
      else if (pos === 'normal') { if (queryState('superscript')) execEditCommand('superscript'); if (queryState('subscript')) execEditCommand('subscript'); }
      return ok;
    });
  }, [editing, inlineScope, runInline]);

  /**
   * Character spacing (Publisher: tracking % of the glyph advance + extra px, scaling % of the glyph width, kerning).
   * Applies to the highlighted text (or the caret's word) while a box is being edited — as inline letter-spacing worked
   * out from that text's font size, scaled words (`.wp-scale`, factor = wanted % / the box's %) and inline font-kerning
   * — otherwise to the whole box. The Character Spacing dialog passes `scope: 'selection'` with every value it holds.
   */
  const setCharacterSpacing = useCallback(({ tracking, letterSpacing, textScale, kerning, kernAbove, scope: forced } = {}) => {
    const tr = tracking ?? p.tracking ?? 100;
    const extra = letterSpacing ?? p.letterSpacing ?? 0;
    // `scope: 'selection'` — the caller (Character Spacing dialog) settled on the highlight when it opened; the live
    // focus (in one of its inputs / buttons by now) must not turn that into a whole-box change.
    const scope = forced === 'selection' && editing ? 'selection' : inlineScope();
    if (scope === 'selection' || scope === 'word') {
      const fs = selectionFontSize(p.fontSize || 16);
      const px = Math.round((trackingToLetterSpacing(tr, fs) + extra) * 100) / 100;
      return runInline(scope, () => {
        const css = { letterSpacing: `${px}px` };
        // kern text at N pt and above: the highlighted text's size decides
        if (kerning !== undefined) css.fontKerning = kerning === false || fs < ptToPx(kernAbove ?? p.kernAbove ?? 14) ? 'none' : 'normal';
        const ok = applyInlineStyle(css);
        if (ok && textScale !== undefined) applyInlineScale((Number(textScale) || 100) / (p.textScale || 100));
        return ok;
      });
    }
    const patch = { tracking: tr, letterSpacing: extra };
    if (textScale !== undefined) patch.textScale = textScale;
    if (kerning !== undefined) patch.kerning = kerning;
    if (kernAbove !== undefined) patch.kernAbove = kernAbove;
    return setProps(patch);
  }, [editing, inlineScope, runInline, p.tracking, p.letterSpacing, p.fontSize, p.textScale, p.kernAbove, setProps]);

  return { textObjs, ids, primary, props: p, editing: !!editing, hasSelection: hasTextSelection(), currentFontFamily, currentFontSize, currentColor, currentLineHeight, characterStyle, paragraphStyle, applyParagraphProps, setLineSpacing, previewLineSpacing, applyInlineProps, toggle, isActive, setFontFamily, previewFontFamily, setFontSize, stepFontSize, setColor, setHighlight, setAlign, alignActive, setList, clearFormatting, applyCase, setProps, setProp, setCharacterSpacing, ensureTextTarget, copy, cut, paste, canPaste };
};
